// Protocol-wide constants. Anything here is part of the wire/crypto contract:
// changing a value is a breaking protocol change, hence the explicit /vN tags.

export const PROTOCOL_VERSION = 1;

// Argon2id parameters for the password KDF. Tuned to be tolerable in a browser
// WASM context on a mid-range device while staying memory-hard. opslimit = passes,
// memlimit = bytes. (Between libsodium's INTERACTIVE and MODERATE presets.)
export const ARGON2_OPSLIMIT = 3;
export const ARGON2_MEMLIMIT = 64 * 1024 * 1024; // 64 MiB

// Byte lengths.
export const SALT_BYTES = 16;
export const KEY_BYTES = 32; // X25519 / symmetric key size
export const XNONCE_BYTES = 24; // XChaCha20-Poly1305 nonce
export const SAFETY_NUMBER_BYTES = 30; // truncated digest for the displayed safety number

// Domain-separation contexts for keyed-hash derivations. Never reuse a context.
export const CTX = {
  wrap: "hush/wrap/v1", // password-derived key-bundle wrapping key
  auth: "hush/auth/v1", // password-derived auth signing seed
  store: "hush/store/v1", // password-derived local-store encryption key
  x3dhRoot: "hush/x3dh/root/v1", // X3DH -> initial root key
  rootKdf: "hush/dr/root/v1", // Double Ratchet root chain step
  chainKdf: "hush/dr/chain/v1", // Double Ratchet symmetric chain step
  msgKey: "hush/dr/msg/v1", // chain key -> message key
  groupChain: "hush/group/chain/v1", // group sender-key chain step
  safety: "hush/safety/v1", // safety-number digest
} as const;

// Bound on cached skipped message keys per session (out-of-order tolerance without
// unbounded memory growth / DoS).
export const MAX_SKIP = 256;

// Disappearing-message TTL presets surfaced in the UI (seconds). 0 = off.
export const TTL_PRESETS = [0, 30, 300, 3600, 86400, 604800] as const;

// Coarse rate limits (per identifier, per window). Strict limiting uses the DO.
export const RATE_LIMITS = {
  register: { max: 5, windowSec: 3600 },
  login: { max: 10, windowSec: 900 },
  send: { max: 600, windowSec: 60 },
} as const;

// Max plaintext attachment size before client-side encryption (32 MiB).
export const MAX_ATTACHMENT_BYTES = 32 * 1024 * 1024;

// Server session + login-challenge lifetimes.
export const SESSION_TTL_MS = 30 * 24 * 3600 * 1000; // 30 days
export const CHALLENGE_TTL_MS = 2 * 60 * 1000; // 2 minutes
